/**
 * Staff-facing explanation of why a provider refused a message, built from `message.error_code`
 * and `message.error_message`.
 *
 * Twilio's status callback carries an ErrorCode but no message text, so SMS rows normally store a
 * bare number. This fills in a plain-language description for the codes programs actually hit,
 * and links anything else to Twilio's public error reference, so staff never need the console.
 */
export type ProviderErrorDetail = {
  code: string | null,
  description: string | null,
  /** Twilio's public reference page for the code. Null for SES codes, which have no such page. */
  docsUrl: string | null,
};

/**
 * Plain-language text for common Twilio SMS and WhatsApp error codes. Not exhaustive: an unlisted
 * code still gets its docs link. English only for now.
 */
export const TWILIO_ERROR_DESCRIPTIONS: Readonly<Record<string, string>> = {
  '21211': 'Invalid phone number',
  '21408': 'Texting to this number\'s country is not enabled',
  '21610': 'Recipient has unsubscribed (replied STOP)',
  '21614': 'Not a mobile number, so it can\'t receive texts',
  '30001': 'Too many messages queued at once; the message was dropped',
  '30002': 'Messaging account suspended',
  '30003': 'Phone is switched off, out of coverage, or can\'t receive texts',
  '30004': 'Blocked; the recipient or their carrier is refusing messages from this number',
  '30005': 'Number is unknown or no longer in service',
  '30006': 'Number is a landline or its carrier can\'t receive texts',
  '30007': 'Blocked by the carrier\'s spam filtering',
  '30008': 'Unknown error from the recipient\'s carrier',
  '30032': 'Toll-free sending number has not been verified',
  '30034': 'Sending number is not registered for A2P 10DLC',
  '63003': 'Recipient is not reachable on WhatsApp',
  '63016': 'Outside WhatsApp\'s 24-hour reply window; only template messages can be sent',
  '63024': 'Invalid WhatsApp recipient',
  '63049': 'Meta chose not to deliver this WhatsApp marketing message',
};

// Twilio codes are all-digit; SES codes (`Permanent/General`, `Reject`, ...) never are.
const isTwilioCode = (code: string) => /^\d+$/.test(code);

/**
 * Null when the row has no error recorded. The stored message wins over the lookup table because
 * it is specific to this message (WhatsApp's ChannelStatusMessage, SES's bounce diagnostic).
 */
export function describeProviderError(
  errorCode: string | null | undefined,
  errorMessage: string | null | undefined,
): ProviderErrorDetail | null {
  const code = errorCode?.trim() || null;
  const message = errorMessage?.trim() || null;
  if (!code && !message) return null;

  const twilio = !!code && isTwilioCode(code);
  return {
    code,
    description: message ?? (twilio ? TWILIO_ERROR_DESCRIPTIONS[code] ?? null : null),
    docsUrl: twilio ? `https://www.twilio.com/docs/api/errors/${code}` : null,
  };
}
